import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { quotationsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import HomeButton from '../components/HomeButton';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Plus, Search, FileText, Loader2, Clock, CheckCircle2,
  XCircle, AlertTriangle, Package, Send, Eye, Trash2
} from 'lucide-react';
import { cn } from '../lib/utils';

const STATUSES = ['All', 'Pending', 'In Progress', 'Quotation Ready', 'Sent to Customer', 'Confirmed', 'Order Raised', 'Rejected'];
const URGENCY_OPTIONS = ['Low', 'Medium', 'High', 'Urgent'];
const DIAMOND_QUALITIES = ['VVS EF', 'GH SI', 'VSSI GH', 'SI-IJ', 'VVSVS GH', 'VVS FG'];
const GOLD_KARATS = ['9kt', '14kt', '18kt'];

const statusConfig = {
  'Pending': { color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-500/20 dark:text-yellow-400', icon: Clock },
  'In Progress': { color: 'bg-blue-100 text-blue-800 dark:bg-blue-500/20 dark:text-blue-400', icon: Loader2 },
  'Quotation Ready': { color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-400', icon: FileText },
  'Sent to Customer': { color: 'bg-purple-100 text-purple-800 dark:bg-purple-500/20 dark:text-purple-400', icon: Send },
  'Confirmed': { color: 'bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-400', icon: CheckCircle2 },
  'Order Raised': { color: 'bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-400', icon: Package },
  'Rejected': { color: 'bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-400', icon: XCircle },
};

const urgencyConfig = {
  'Low': 'bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-400',
  'Medium': 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400',
  'High': 'bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-400',
  'Urgent': 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400',
};

function formatDate(dateStr) {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return '-'; }
}

export default function Quotations() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);

  // Create form state
  const [formData, setFormData] = useState({
    customer_name: '',
    diamond_quality: '',
    gold_karat: '',
    urgency: 'Medium',
    special_instructions: '',
    requested_by: '',
    products: [{ style_number: '', customisation_notes: '', quantity: 1 }],
  });

  const fetchQuotations = async () => {
    try {
      const res = await quotationsApi.getAll();
      setQuotations(res.data);
    } catch (err) {
      toast.error('Failed to load quotations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchQuotations(); }, []);

  const filtered = useMemo(() => {
    let result = quotations;
    if (statusFilter !== 'All') result = result.filter(q => q.status === statusFilter);
    if (search) {
      const s = search.toLowerCase();
      result = result.filter(q =>
        q.customer_name.toLowerCase().includes(s) ||
        q.products.some(p => p.style_number.toLowerCase().includes(s)) ||
        (q.saleshub_reference_id || '').toLowerCase().includes(s) ||
        (q.requested_by || '').toLowerCase().includes(s)
      );
    }
    return result;
  }, [quotations, statusFilter, search]);

  const statusCounts = useMemo(() => {
    const counts = {};
    quotations.forEach(q => { counts[q.status] = (counts[q.status] || 0) + 1; });
    return counts;
  }, [quotations]);

  const addProductRow = () => {
    setFormData(prev => ({
      ...prev,
      products: [...prev.products, { style_number: '', customisation_notes: '', quantity: 1 }]
    }));
  };

  const removeProductRow = (idx) => {
    if (formData.products.length <= 1) return;
    setFormData(prev => ({
      ...prev,
      products: prev.products.filter((_, i) => i !== idx)
    }));
  };

  const updateProduct = (idx, field, value) => {
    setFormData(prev => ({
      ...prev,
      products: prev.products.map((p, i) => i === idx ? { ...p, [field]: value } : p)
    }));
  };

  const handleCreate = async () => {
    if (!formData.customer_name.trim()) {
      toast.error('Customer name is required');
      return;
    }
    if (formData.products.every(p => !p.style_number.trim())) {
      toast.error('At least one style number is required');
      return;
    }
    if (!formData.requested_by.trim()) {
      toast.error('Salesperson name is required');
      return;
    }

    setCreating(true);
    try {
      const payload = {
        ...formData,
        products: formData.products.filter(p => p.style_number.trim()),
      };
      await quotationsApi.create(payload);
      toast.success('Quotation request created');
      setShowCreateDialog(false);
      setFormData({
        customer_name: '', diamond_quality: '', gold_karat: '', urgency: 'Medium',
        special_instructions: '', requested_by: '', products: [{ style_number: '', customisation_notes: '', quantity: 1 }],
      });
      fetchQuotations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create quotation');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (e, quotationId) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this quotation request?')) return;
    try {
      await quotationsApi.delete(quotationId);
      toast.success('Quotation deleted');
      fetchQuotations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-[1400px] mx-auto" data-testid="quotations-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <HomeButton />
          <div>
            <h1 className="text-2xl font-heading font-bold text-slate-900 dark:text-white">Quotations</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Manage quotation requests from SalesHub and internal team
            </p>
          </div>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} data-testid="create-quotation-btn">
          <Plus className="h-4 w-4 mr-2" /> New Quotation Request
        </Button>
      </div>

      {/* Status Counts */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 mb-6">
        {STATUSES.filter(s => s !== 'All').map(status => {
          const cfg = statusConfig[status];
          const Icon = cfg.icon;
          return (
            <button
              key={status}
              onClick={() => setStatusFilter(statusFilter === status ? 'All' : status)}
              className={cn(
                "flex items-center gap-2 p-2.5 rounded-lg border transition-all text-left",
                statusFilter === status
                  ? "border-amber-400 bg-amber-50 dark:bg-amber-500/10 dark:border-amber-500/50"
                  : "border-slate-200 dark:border-neutral-800 hover:border-slate-300 dark:hover:border-neutral-700"
              )}
              data-testid={`filter-${status.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <Icon className="h-3.5 w-3.5 flex-shrink-0 text-slate-500 dark:text-slate-400" />
              <div className="min-w-0">
                <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{status}</p>
                <p className="text-sm font-semibold text-slate-900 dark:text-white">{statusCounts[status] || 0}</p>
              </div>
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="flex gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search by customer, style number, or reference..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="quotation-search"
          />
        </div>
      </div>

      {/* Table */}
      <Card className="border border-slate-200 dark:border-neutral-800">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">
              {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="h-12 w-12 mx-auto text-slate-300 dark:text-slate-600 mb-3" />
              <p className="text-slate-500 dark:text-slate-400">
                {quotations.length === 0 ? 'No quotation requests yet' : 'No quotations match your filters'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/50 dark:bg-neutral-900/50">
                    <TableHead className="font-semibold">Customer</TableHead>
                    <TableHead className="font-semibold">Products</TableHead>
                    <TableHead className="font-semibold">Diamond</TableHead>
                    <TableHead className="font-semibold">Gold</TableHead>
                    <TableHead className="font-semibold">Urgency</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                    <TableHead className="font-semibold">Requested By</TableHead>
                    <TableHead className="font-semibold">Date</TableHead>
                    <TableHead className="font-semibold text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((q) => {
                    const sCfg = statusConfig[q.status] || statusConfig['Pending'];
                    const SIcon = sCfg.icon;
                    const totalPcs = q.products.reduce((sum, p) => sum + (p.quantity || 0), 0);
                    return (
                      <TableRow
                        key={q.id}
                        className="cursor-pointer hover:bg-slate-50 dark:hover:bg-neutral-900/50 transition-colors"
                        onClick={() => navigate(`/quotations/${q.id}`)}
                        data-testid={`quotation-row-${q.id}`}
                      >
                        <TableCell>
                          <div>
                            <p className="font-medium text-slate-900 dark:text-white">{q.customer_name}</p>
                            {q.saleshub_reference_id && (
                              <p className="text-xs text-slate-400">Ref: {q.saleshub_reference_id}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <span className="font-medium">{q.products.length}</span>
                            <span className="text-slate-400 ml-1">styles</span>
                            <span className="text-slate-300 dark:text-slate-600 mx-1">|</span>
                            <span className="font-medium">{totalPcs}</span>
                            <span className="text-slate-400 ml-1">pcs</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">{q.diamond_quality || '-'}</TableCell>
                        <TableCell className="text-sm">{q.gold_karat || '-'}</TableCell>
                        <TableCell>
                          <Badge variant="secondary" className={cn("text-xs", urgencyConfig[q.urgency])}>
                            {q.urgency}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className={cn("text-xs flex items-center gap-1 w-fit", sCfg.color)}>
                            <SIcon className="h-3 w-3" />
                            {q.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                            {q.requested_by || '-'}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">{formatDate(q.created_at)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); navigate(`/quotations/${q.id}`); }}
                              data-testid={`view-quotation-${q.id}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            {q.status !== 'Order Raised' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10"
                                onClick={(e) => handleDelete(e, q.id)}
                                data-testid={`delete-quotation-${q.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">New Quotation Request</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Customer Name & Salesperson */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Customer Name *</Label>
                <Input
                  value={formData.customer_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, customer_name: e.target.value }))}
                  placeholder="Enter customer name"
                  data-testid="quotation-customer-name"
                />
              </div>
              <div>
                <Label>Salesperson Name *</Label>
                <Input
                  value={formData.requested_by}
                  onChange={(e) => setFormData(prev => ({ ...prev, requested_by: e.target.value }))}
                  placeholder="Name of the salesperson"
                  data-testid="quotation-requested-by"
                />
              </div>
            </div>

            {/* Diamond Quality & Gold Karat */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Diamond Quality</Label>
                <Select value={formData.diamond_quality} onValueChange={(v) => setFormData(prev => ({ ...prev, diamond_quality: v }))}>
                  <SelectTrigger data-testid="quotation-diamond-quality">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {DIAMOND_QUALITIES.map(dq => <SelectItem key={dq} value={dq}>{dq}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Gold Karat</Label>
                <Select value={formData.gold_karat} onValueChange={(v) => setFormData(prev => ({ ...prev, gold_karat: v }))}>
                  <SelectTrigger data-testid="quotation-gold-karat">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {GOLD_KARATS.map(gk => <SelectItem key={gk} value={gk}>{gk}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Urgency */}
            <div>
              <Label>Urgency</Label>
              <Select value={formData.urgency} onValueChange={(v) => setFormData(prev => ({ ...prev, urgency: v }))}>
                <SelectTrigger data-testid="quotation-urgency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {URGENCY_OPTIONS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Products */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Products / Style Numbers *</Label>
                <Button variant="outline" size="sm" onClick={addProductRow} data-testid="add-product-row">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add Row
                </Button>
              </div>
              <div className="space-y-2">
                {formData.products.map((product, idx) => (
                  <div key={idx} className="flex gap-2 items-start" data-testid={`product-row-${idx}`}>
                    <div className="flex-1">
                      <Input
                        placeholder="Style Number"
                        value={product.style_number}
                        onChange={(e) => updateProduct(idx, 'style_number', e.target.value)}
                        data-testid={`product-style-${idx}`}
                      />
                    </div>
                    <div className="flex-1">
                      <Input
                        placeholder="Customisation Notes"
                        value={product.customisation_notes}
                        onChange={(e) => updateProduct(idx, 'customisation_notes', e.target.value)}
                        data-testid={`product-notes-${idx}`}
                      />
                    </div>
                    <div className="w-20">
                      <Input
                        type="number"
                        min="1"
                        placeholder="Qty"
                        value={product.quantity}
                        onChange={(e) => updateProduct(idx, 'quantity', parseInt(e.target.value) || 1)}
                        data-testid={`product-qty-${idx}`}
                      />
                    </div>
                    {formData.products.length > 1 && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-500 hover:text-red-600 h-9 w-9 flex-shrink-0"
                        onClick={() => removeProductRow(idx)}
                        data-testid={`remove-product-${idx}`}
                      >
                        <XCircle className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Special Instructions */}
            <div>
              <Label>Special Instructions</Label>
              <Textarea
                value={formData.special_instructions}
                onChange={(e) => setFormData(prev => ({ ...prev, special_instructions: e.target.value }))}
                placeholder="Any special requirements or instructions..."
                rows={3}
                data-testid="quotation-instructions"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={creating} data-testid="submit-quotation-btn">
              {creating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
